import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";

const AuthContext = createContext(null);

// Public marketing routes — we skip the /auth/me probe on cold visits here
// when no prior session hint exists, to keep the public DevTools console clean.
const PUBLIC_PREFIXES = [
  "/", "/pricing", "/about", "/faq", "/security", "/safety", "/contact",
  "/essays", "/editorial", "/archetypes", "/research", "/search",
  "/terms", "/privacy", "/login", "/register", "/forgot-password", "/reset-password",
];
function isPublicPath(pathname) {
  if (!pathname) return false;
  if (pathname === "/") return true;
  return PUBLIC_PREFIXES.some((p) => p !== "/" && (pathname === p || pathname.startsWith(p + "/")));
}
function hasSessionHint() {
  try { return localStorage.getItem("ss_session") === "1"; } catch { return false; }
}

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);
  const [ambassador, setAmbassador] = useState(null);
  const [ready, setReady] = useState(false);

  const refreshMe = useCallback(async () => {
    // Skip the probe on public routes for cold visitors — keeps the console clean
    // and shaves one network round-trip from the marketing pages.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (!hasSessionHint() && isPublicPath(path)) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setAmbassador(data.ambassador || null);
      try { localStorage.setItem("ss_session", "1"); } catch {}
    } catch {
      setUser(false);
      setAmbassador(null);
      try { localStorage.removeItem("ss_session"); } catch {}
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      try { localStorage.setItem("ss_session", "1"); } catch {}
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: getErrorMessage(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      try { localStorage.setItem("ss_session", "1"); } catch {}
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: getErrorMessage(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
    try { localStorage.removeItem("ss_session"); } catch {}
    // Ask drafts are per-user client state — never let them survive into another account on a shared device
    try { Object.keys(localStorage).filter((k) => k.startsWith("ss_ask_draft")).forEach((k) => localStorage.removeItem(k)); } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, ambassador, ready, login, register, logout, refreshMe, refresh: refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
